// Grammar Units 2 Data - Unit 2: English Tense Learning
// Based on Ch02 英文時態學習.jsx
// Organized for Supabase migration and systematic data management

export const grammarUnits2Data = [
  {
    id: 2,
    title: { traditional: '英文時態學習', simplified: '英文时态学习', english: 'English Tense Learning' },
    subtitle: { traditional: '掌握各種時態的用法', simplified: '掌握各种时态的用法', english: 'Master the usage of various tenses' },
    description: { traditional: '系統性學習英文動詞時態，包括現在式、過去式、未來式以及各種進行式和完成式。', simplified: '系统性学习英文动词时态，包括现在式、过去式、未来式以及各种进行式和完成式。', english: 'Systematically learn English verb tenses including present, past, future tenses and various progressive and perfect forms.' },
    icon: '⏰',
    color: 'from-green-500 to-emerald-600',
    status: 'notStarted',
    difficulty: 'beginner',
    estimatedHours: 5,
    content: {
      overview: {
        title: "英文動詞時態概覽",
        description: "因時間不同而分：現在式、過去式、未來式\n因動作形式不同而分：簡單式、進行式、完成式、完成進行式",
        timelineData: [
          { tense: "簡單式", present: "He writes.", past: "He wrote.", future: "He will write." },
          { tense: "進行式", present: "He is writing.", past: "He was writing.", future: "He will be writing." },
          { tense: "完成式", present: "He has written.", past: "He had written.", future: "He will have written." },
          { tense: "完成進行式", present: "He has been writing.", past: "He had been writing.", future: "He will have been writing." }
        ]
      },
      pastSimple: {
        title: "過去簡單式 (Past Simple)",
        structure: "主詞 + 過去式動詞",
        sections: [
          {
            name: "過去簡單式結構",
            structure: "主詞 + 過去式動詞",
            negativeStructure: "主詞 + did not/didn't + 原形動詞",
            questionStructure: "Did + 主詞 + 原形動詞",
            usage: "用於下列情況中：",
            examples: [
              "Yesterday afternoon I stayed at home and studied for the math test. I finished at 7:00. Then I called my friend Jerry, and we talked for two hours.",
              "昨天下午我待在家裡準備數學考試。我七點完成。然後我打電話給朋友傑瑞，我們聊了兩個小時。"
            ]
          },
          {
            name: "用法 5-1：敘述過去發生的事，或歷史上的事實",
            usage: "常搭配表示過去時間的副詞、副詞片語或副詞子句使用。",
            examples: [
              "He ordered a pizza for dinner last night. 他昨天晚上點了一份披薩當晚餐。",
              "Columbus discovered the New World in 1492. 哥倫布在一四九二年發現了新大陸。"
            ]
          },
          {
            name: "用法 5-2：敘述過去的習慣、過去的常態",
            usage: "可搭配 always、usually、often、sometimes 等頻率副詞使用。",
            examples: [
              "We usually walked in the park before dinner. 我們以前通常會在晚餐前到公園散步。"
            ]
          }
        ]
      },
      pastContinuous: {
        title: "過去進行式 (Past Continuous)",
        structure: "主詞 + was/were + V-ing",
        sections: [
          {
            name: "過去進行式結構",
            structure: "主詞 + was/were + V-ing",
            negativeStructure: "主詞 + was/were + not + V-ing 或 主詞 + wasn't/weren't + V-ing",
            questionStructure: "Was/Were + 主詞 + V-ing",
            examples: [
              "He was visiting an art museum about this time last week. 他上星期這個時候正在參觀美術館。"
            ]
          },
          {
            name: "用法 6-1：敘述發生在過去持續進行的動作",
            usage: "中文常用「正在......」來表示。",
            examples: [
              "He was working at McDonald's at that time. 那段時間他在麥當勞工作。",
              "He was visiting an art museum about this time last week. 上星期這個時候他正在參觀美術館。"
            ]
          },
          {
            name: "用法 6-2：表在過去某一個動作發生時，另一個動作正在進行中",
            usage: "可配合指出那段時間的副詞、副詞片語或副詞子句。",
            examples: [
              "He was organizing his CD collection when the phone rang. 電話鈴響時，他正在整理他收藏的 CD。",
              "She was cooking dinner when the man appeared. 那個男人出現時她正在煮晚餐。",
              "→ While she was cooking dinner, the man appeared. 那個男人出現時她正在煮晚餐。"
            ],
            note: "兩件事或兩動作一前一後發生，都用簡單式表示。",
            additionalExample: "When Jane heard the news, she started to cry. 珍聽到這個消息時，便開始哭了起來。"
          }
        ]
      },
      pastPerfect: {
        title: "過去完成式 (Past Perfect)",
        structure: "主詞 + had + p.p.",
        sections: [
          {
            name: "過去完成式結構",
            structure: "主詞 + had + p.p.",
            negativeStructure: "主詞 + had not/hadn't + p.p.",
            questionStructure: "Had + 主詞 + p.p.",
            examples: [
              "Before I bought the computer, I had played few video games. 在我買電腦以前，我從未玩過電玩。"
            ]
          },
          {
            name: "用法 7-1：通常會搭配過去簡單式一起使用，用以區分先後順序",
            usage: "較早發生：過去完成式，較晚發生：過去簡單式",
            timeline: "過去B 10:00 → 過去A 13:00 → 現在 15:00",
            examples: [
              "Dad had finished his dinner by the time I got home. 爸爸在我到家前就吃完晚餐了。",
              "The fire started because I had forgotten to turn off the oven. 起火的原因是因為我忘了關火爐。",
              "He told me that he had met me somewhere two years before. 他告訴我他兩年前曾經在某個地方見過我。"
            ]
          },
          {
            name: "用法 7-2：和現在完成式用法相同，用於強調動作的完成",
            usage: "常與 ever、for、just、already、never 等字詞運用。",
            examples: [
              "The band had already played his favorite song before he got to the show. 在他到達表演現場前，樂團就已經演奏了他最喜歡的一首歌。",
              "Before I bought the computer, I had never played any video games. 在我買電腦以前，我從未玩過電玩。"
            ]
          },
          {
            name: "觀念釐清",
            clarification: [
              "1. 可用連接詞區分兩件事情的先後，如：before、after。故除了用過去完成式之外，也可只用過去簡單式。",
              "Ex. • He cleaned his room before he started his homework. 他在開始做功課前就把房間整理乾淨了。",
              "• Donna quit the band after she hurt her hand. 唐娜手受傷後就離開了樂團。",
              "",
              "2. 如果句子裡有一連串動作，而且由上下文可以看出其先後順序，那麼這一連串的動作可以都用過去簡單式表示，只需在最後一個動詞前加上 and 即可。",
              "Ex. • He washed the potatoes, peeled the carrots, put the roast in the oven, and started to prepare the dessert. 他把馬鈴薯洗乾淨、紅蘿蔔削皮、烤肉放入烤箱，然後開始準備甜點。"
            ]
          }
        ]
      },
      pastPerfectContinuous: {
        title: "過去完成進行式 (Past Perfect Continuous)",
        structure: "主詞 + had been + V-ing",
        sections: [
          {
            name: "過去完成進行式結構",
            structure: "主詞 + had been + V-ing",
            negativeStructure: "主詞 + had not/hadn't + been + V-ing",
            questionStructure: "Had + 主詞 + been + V-ing",
            examples: [
              "Before I got home, my mom had been preparing dinner for half an hour. 在我到家前，我媽媽已經準備晚餐半個鐘頭了。"
            ]
          },
          {
            name: "用法 8-1：使用時機跟過去完成式差不多，差別在於更強調動作的持續性",
            usage: "假設發生在過去的兩件事 A、B，若欲強調較早發生的 A 的持續性，則用過去完成進行式。",
            examples: [
              "By the time I arrived at the office, Jim had been working for two hours already. 在我到辦公室的時候，吉姆已經工作了兩個小時。",
              "We had been waiting for over an hour when she showed up. 她出現時，我們已經等了超過一個鐘頭了。",
              "Before I got home, my mom had been preparing dinner for half an hour. 在我到家前，我媽媽已經準備晚餐半個鐘頭了。"
            ]
          },
          {
            name: "用法 8-2：常與 before、by the time、for、how long 等搭配使用",
            examples: [
              "Before he went to college, he had been working as a lifeguard. 他在上大學之前，一直都在當救生員。"
            ]
          }
        ]
      },
      futureSimple: {
        title: "未來簡單式 (Future Simple)",
        structure: "主詞 + will + 原形動詞 或 主詞 + be going to + 原形動詞",
        sections: [
          {
            name: "未來簡單式結構",
            affirmativeStructure: "主詞 + will + 原形動詞 或 主詞 + be going to + 原形動詞",
            negativeStructure: "主詞 + will not/won't + 原形動詞 或 主詞 + be not going to + 原形動詞",
            questionStructure: "Will + 主詞 + 原形動詞 或 Be + 主詞 + going to + 原形動詞",
            examples: [
              "a.) Things will definitely get better. 事情一定會好轉的。",
              "b.) I am going to buy a new computer. 我不打算買新電腦。"
            ]
          },
          {
            name: "用法 9-1：未來會發生的事",
            usage: "敘述「認為未來會發生的事，或希望未來會發生的事」。",
            examples: [
              "Things will definitely get better. 事情一定會好轉的。"
            ]
          },
          {
            name: "用法 9-2：預測未來會發生的事",
            usage: "敘述「根據一般的看法、想法、態度等，預測未來會發生的事」。",
            examples: [
              "I am not going to buy a new computer. 我不打算買新電腦。"
            ]
          },
          {
            name: "用法 9-3：決定未來要做的事",
            usage: "敘述「決定在未來要做的事，或未來想做的事」。",
            examples: [
              "I promise I will help you as long as you are in need. 我會儘只要你有需要就會幫你忙。"
            ]
          },
          {
            name: "觀念釐清：be going to + V. 與 will + V. 的差別",
            differences: [
              {
                type: "be going to + 原形動詞",
                usage: "表示已經決定要做的事，或根據現在的事實或情況，預測未來十分有可能發生的事。",
                example: "I can see black clouds. It's going to rain. 我看得到烏雲，就快要下雨了。"
              },
              {
                type: "will + 原形動詞", 
                usage: "表示承諾會去做某事，或當下承諾要做某事。",
                example: "I will bring some groceries home. 我會帶一些日用品回家。"
              }
            ]
          },
          {
            name: "用法 9-4：條件句或時間副詞子句中，提到未來的事情",
            usage: "在時間副詞子句或條件句中，就算提到未來的事情，也要用現在簡單式表示，不過主子句要用未來式。",
            examples: [
              "When you arrive here next week, I will already be in Germany. 你下週到這裡時，我將已經在德國了。",
              "We will be late if we don't start now. 如果我們現在不開始的話會來不及。"
            ]
          },
          {
            name: "用法 9-5：談「未來」的用法",
            futureExpressions: [
              {
                expression: "be going to",
                usage: "be going to 主要用在將要發生的事情。",
                example: "It is going to rain; you'd better take an umbrella with you. 快要下雨了，你最好帶把傘。"
              },
              {
                expression: "be to",
                usage: "be to 主要為英式英語用法，用在帶有計畫、預定、義務、命運等情況。",
                example: "I am to meet you at seven o'clock in front of the theater. 我七點鐘會在戲院前面跟你碰面。"
              }
            ]
          },
          {
            name: "表示未來的時間，可用不同的時態表示",
            examples: [
              "She will leave for Australia tomorrow. 明天她將動身前往澳洲。",
              "→ is going to leave",
              "→ is leaving", 
              "→ leaves"
            ]
          },
          {
            name: "be about to 用在瞬間即將要發生的事",
            examples: [
              "Watch! Lee is about to do a magic trick. 注意看！李即將要表演魔術了。"
            ]
          }
        ]
      },
      futureContinuous: {
        title: "未來進行式 (Future Continuous)",
        structure: "主詞 + will be + V-ing",
        sections: [
          {
            name: "未來進行式結構",
            affirmativeStructure: "主詞 + will be + V-ing",
            negativeStructure: "主詞 + will not/won't + be + V-ing",
            questionStructure: "Will + 主詞 + be + V-ing",
            examples: [
              "We will be accepting applications until the end of the month. 月底前我們都會接受申請。"
            ]
          },
          {
            name: "用法 10-1：表示在未來的某時前後會持續進行的動作",
            usage: "常搭配表示未來時間的副詞、副詞片語或副詞子句。",
            examples: [
              "The band will be performing on this coming Saturday and Sunday. 這支樂團將在這星期六和星期天表演。",
              "We will be accepting applications until the end of the month. 月底前我們都會接受申請。"
            ]
          },
          {
            name: "用法 10-2：表示在未來有一個動作發生時，另一個動作正持續進行",
            examples: [
              "When you come tonight, I'll probably be correcting my students' papers. 今晚你過來時，我可能正在批改學生的考卷。"
            ]
          },
          {
            name: "用法 10-3：禮貌性地詢問別人是否將有什麼計畫時，可用未來進行式表示",
            usage: "通常這個計畫與詢問者有關",
            examples: [
              "A: Will you be needing the car this evening, sir? 先生，您今天晚上要用車嗎？",
              "B: No, I plan on staying in for the night. 不用，我今晚打算待在家裡。"
            ]
          },
          {
            name: "比較",
            comparison: "請試著說明以下兩句有何不同：",
            examples: [
              "• He was watching TV when I arrived. 我到的那時候，他正在看電視。（已發生）",
              "• He will be watching TV when I arrive. 我到的時候，他可能會在看電視。（未來可能）"
            ]
          }
        ]
      },
      futurePerfect: {
        title: "未來完成式 (Future Perfect)",
        structure: "主詞 + will have + p.p.",
        sections: [
          {
            name: "未來完成式結構",
            affirmativeStructure: "主詞 + will have + p.p.",
            negativeStructure: "主詞 + will not/won't + have + p.p.",
            questionStructure: "Will + 主詞 + have + p.p.",
            examples: [
              "We will have made 200 calls by the end of the day. 今天結束前我們將會打兩百通電話。"
            ]
          },
          {
            name: "用法 11-1：表示在未來某個時間點之前會完成的動作",
            usage: "常配合表未來時間的副詞或子句，多與 by、by the time、before 等詞語一起使用",
            examples: [
              "By the time we get there, he will have already left. 我們到那裡之前，他將已經離開了。",
              "We will have made 200 calls by the end of the day. 今天結束前我們將會打兩百通電話。",
              "By the time you retire, you will have saved ten million dollars. 到你退休前，你將會存到一千萬。",
              "Before the police can get here, the thief will have escaped. 在警方到達這裡之前，小偷將早就逃之夭夭了。"
            ]
          }
        ]
      },
      futurePerfectContinuous: {
        title: "未來完成進行式 (Future Perfect Continuous)",
        structure: "主詞 + will have been + V-ing",
        sections: [
          {
            name: "未來完成進行式結構",
            affirmativeStructure: "主詞 + will have been + V-ing",
            negativeStructure: "主詞 + will not/won't + have been + V-ing",
            questionStructure: "Will + 主詞 + have been + V-ing",
            examples: [
              "By next June, I'll have been teaching English in this high school for 30 years. 到明年六月之前，我將已經在這所高中教英文三十年了。"
            ]
          },
          {
            name: "用法 12-1：在未來某個時間點之前會完成的動作，特別強調該動作仍將持續進行",
            examples: [
              "By the end of the month, this power plant will have been producing power for six years. 到月底前，這座發電廠將會持續發電六年了。"
            ]
          },
          {
            name: "用法 12-2：常與 before、by、by the time 等合用",
            examples: [
              "By next June, I'll have been teaching English in this high school for 30 years. 到明年六月之前，我將已經在這所高中教英文三十年了。"
            ]
          }
        ]
      },
      presentSimple: {
        title: "現在簡單式詳解",
        structure: "主詞 + V/V-s/V-es",
        sections: [
          {
            name: "現在簡單式用法",
            structure: "主詞 + V/V-s/V-es",
            usage: "1. 敘述固定的、習慣性的行為\n2. 敘述一般的事實、不變的真理、格言、諺語\n3. 敘述現在的心理狀態、現在的狀況、現在的動作\n4. 敘述未來會發生的事，但現在就預定或安排好了",
            examples: [
              "Julia always comes to work on time. 茱莉雅總是準時上班。",
              "Lucy and Sam visit their grandparents every Sunday. 露西和山姆每個星期天會去看祖父母。",
              "Honesty is the best policy. 誠實是最上策。"
            ],
            stativeVerbs: {
              emotion: "dislike, hate, like, love",
              cognition: "believe, know, mean, realize, remember, think, understand, mind, want, need, forget, hope",
              perception: "feel, hear, see, sound, smell, taste",
              possession: "belong to, have, own, possess, remain, exist"
            }
          },
          {
            name: "現在進行式用法",
            structure: "主詞 + am/is/are + V-ing",
            usage: "1. 敘述現在正在持續進行的動作\n2. 敘述某種暫時的、只維持一段時間的狀態\n3. 敘述正在改變、發展、或進行中的事\n4. 敘述未來的事情現在已經預定、或安排未來將會發生",
            examples: [
              "I'm reading a storybook. 我正在看故事書。",
              "Jim is living in New York at the moment. 吉姆目前住在紐約。",
              "The children are growing up quickly. 孩子們成長的速度很快。"
            ]
          },
          {
            name: "現在完成進行式用法",
            structure: "has/have been + V-ing",
            usage: "和現在完成式一樣，常會和 for、since 等搭配使用",
            examples: [
              "She has been practicing yoga for five years. 她練瑜珈已經持續五年了。",
              "She has been performing since she graduated from college. 她從大學畢業後就一直在表演。"
            ]
          }
        ]
      },
      exercises: {
        title: "練習題",
        sections: [
          {
            name: "時態練習 1",
            questions: [
              {
                question: "1. Nobody can tell __________ in the future.",
                options: {
                  A: "what has happened",
                  B: "what will happen", 
                  C: "what happened",
                  D: "what happens"
                },
                answer: "B"
              },
              {
                question: "2. Louis and Freddy __________ after they get off work.",
                options: {
                  A: "came over",
                  B: "comes over",
                  C: "is going to come over", 
                  D: "will come over"
                },
                answer: "D"
              }
            ]
          },
          {
            name: "時態練習 2",
            questions: [
              {
                question: "1. The police __________ the thief all night.",
                options: {
                  A: "chasing",
                  B: "have been chasing",
                  C: "has chased", 
                  D: "were chased"
                },
                answer: "B"
              },
              {
                question: "2. He __________ for this marathon since last year.",
                options: {
                  A: "have trained",
                  B: "trained",
                  C: "was trained",
                  D: "has been training"
                },
                answer: "D"
              }
            ]
          }
        ]
      }
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits2Data.find(unit => unit.id === unitId);
};

// Export units by difficulty
export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits2Data.filter(unit => unit.difficulty === difficulty);
};

// Export completed units
export const getCompletedUnits = () => {
  return grammarUnits2Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits2Data;
